import React from 'react';

import { Form, message } from 'antd';
import { useTranslation } from 'react-i18next';

import { NSKeys } from '@app/i18n';
import { RetrieveUserGroupSchema } from '@app/models/users';
import { getFormErrorsFromResponse } from '@app/services/getFormErrorsFromResponse';

import { useCreateGroupQuery, useUpdateGroupQuery } from '../../../../hooks';
import { useGroups } from '../../../../providers';
import { NEW_GROUP_ID } from '../../../../providers/constants';
import { EditableContext } from '../EditableContext';

interface IProps {
  index: number;
  record: RetrieveUserGroupSchema;
}

export const EditableRow: React.FC<IProps> = ({ index, record, ...props }) => {
  void index;
  const [form] = Form.useForm();
  const validationT = useTranslation(NSKeys.validation);
  const commonT = useTranslation(NSKeys.common);
  const { onEditEnd, onSaveSuccess } = useGroups();
  const { mutate: createGroup, failureReason: createFailureReason } = useCreateGroupQuery();
  const { mutate: updateGroup, failureReason: updateFailureReason } = useUpdateGroupQuery();

  React.useEffect(() => {
    if (record) {
      form.setFieldsValue(record);
    }
  }, [form, record]);

  React.useEffect(() => {
    if (createFailureReason) {
      const formValues = form.getFieldsValue();
      const { pointerErrors } = getFormErrorsFromResponse(formValues, createFailureReason, validationT.t, true);
      message.error(pointerErrors.length ? pointerErrors?.[0]?.errors?.join(', ') : commonT.t('somethingWentWrong'));
    }
  }, [createFailureReason, form, validationT.t]);

  React.useEffect(() => {
    if (updateFailureReason) {
      const formValues = form.getFieldsValue();
      const { pointerErrors } = getFormErrorsFromResponse(formValues, updateFailureReason, validationT.t, true);
      message.error(pointerErrors.length ? pointerErrors?.[0]?.errors?.join(', ') : commonT.t('somethingWentWrong'));
    }
  }, [updateFailureReason, form, validationT.t]);

  const handleFinish = (formValues: RetrieveUserGroupSchema) => {
    if (record.uid === NEW_GROUP_ID) {
      createGroup(
        { name: formValues.name, cluster: formValues.cluster || null },
        {
          onSuccess: () => {
            onSaveSuccess();
            onEditEnd(record.uid);
          },
        }
      );
      return;
    }

    if (record.name !== formValues.name || record.cluster !== formValues.cluster) {
      updateGroup(
        { id: record.uid, data: { name: formValues.name, cluster: formValues.cluster || null } },
        {
          onSuccess: () => {
            onEditEnd(record.uid);
          },
        }
      );
      return;
    }

    onEditEnd(record.uid);
  };

  return (
    <Form form={form} component={false} onFinish={handleFinish}>
      <EditableContext.Provider value={form}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  );
};
